import React, { useState, useEffect, Suspense, useRef } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, useGLTF, Html, useProgress, PerspectiveCamera } from '@react-three/drei';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Box, Typography, IconButton, Tooltip } from '@mui/material';
import ModelLoader from 'component/Loader/ModelLoader';
import { ZoomIn, ZoomOut, CenterFocusStrong, ThreeDRotation, PanTool, ThreeSixty, ArrowBack } from '@mui/icons-material';
import ToggleSwitch from 'component/toggle-switch';

// Model URLs
const TIRA_MODEL_URL =
  'https://stdishadev2.blob.core.windows.net/tira-digital-twin/TIRA_final_v2.0.glb?sp=r&st=2025-04-21T04:56:37Z&se=2026-04-21T12:56:37Z&spr=https&sv=2024-11-04&sr=b&sig=%2BqahgfNbefLqhv%2FHZs99pYQZA6%2BFZdjtZhQ3LAaTrCI%3D';
const modelURLs = {
  ideal: {
    SHELF_1: [
      'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Ideal/Ideal%20SHELF_1.glb?sp=r&st=2025-04-21T04:56:55Z&se=2026-04-21T12:56:55Z&spr=https&sv=2024-11-04&sr=b&sig=1TWNYkweKgg4GrOAulHsufkGB3lkmjpfsQrgwZrY6JE%3D'
    ],
    SHELF_2: [
      'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Ideal/Ideal%20SHELF_2.glb?sp=r&st=2025-04-21T04:57:25Z&se=2026-04-21T12:57:25Z&spr=https&sv=2024-11-04&sr=b&sig=6%2BMBySIMI3MTxXzCQI1TInNf5oS9Vgh8aQSx0mUwgkk%3D'
    ],
    SHELF_3: [
      'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Ideal/Ideal%20SHELF_3.glb?sp=r&st=2025-04-21T04:57:40Z&se=2026-04-21T12:57:40Z&spr=https&sv=2024-11-04&sr=b&sig=5TOrLrEJ5P6lNcOMruKUugzmqXGnHDwltNHAZf%2FqLB8%3D'
    ],
    SHELF_4: [
      'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Ideal/Ideal%20SHELF_4.glb?sp=r&st=2025-04-21T04:57:53Z&se=2026-04-21T12:57:53Z&spr=https&sv=2024-11-04&sr=b&sig=obOZFR7S56Wu3BUGlNf2eFx7uNF64IAxtVrKWlsJ4%2FQ%3D'
    ],
    SHELF_5: [
      'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Ideal/Ideal%20SHELF_5.glb?sp=r&st=2025-04-21T04:58:10Z&se=2026-04-21T12:58:10Z&spr=https&sv=2024-11-04&sr=b&sig=wpwgfHLJW76x3cM2f1%2FEtgS%2FABITHyh2zAM39sqlRO0%3D'
    ],
    SHELF_6: [
      'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Ideal/Ideal%20SHELF_6.glb?sp=r&st=2025-04-21T04:58:22Z&se=2026-04-21T12:58:22Z&spr=https&sv=2024-11-04&sr=b&sig=hOWBvJ6zwrPaBNyOGUgfd4%2FH1GY976VexZVOIwbNRP8%3D'
    ],
    SHELF_7: [
      'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Ideal/Ideal%20SHELF_7.glb?sp=r&st=2025-04-21T04:58:53Z&se=2026-04-21T12:58:53Z&spr=https&sv=2024-11-04&sr=b&sig=VFBzanK%2BxeTwwGQQ06q2xaLsXuV600fyKIjP9t6JIj0%3D'
    ],
    SHELF_8: [
      'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Ideal/Ideal%20SHELF_8.glb?sp=r&st=2025-04-21T04:59:07Z&se=2026-04-21T12:59:07Z&spr=https&sv=2024-11-04&sr=b&sig=8hkKcy35DJE3i09n%2F92A2zBz9pnurJ7V6Tsh4cI6y7Q%3D'
    ],
    SHELF_9: [
      'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Ideal/Ideal%20SHELF_9.glb?sp=r&st=2025-04-21T04:59:20Z&se=2026-04-21T12:59:20Z&spr=https&sv=2024-11-04&sr=b&sig=J256fVo7TQu9vm3A6SJHanmeDodmHDZ60D8vkX8cIsE%3D'
    ],
    SHELF_10: [
      'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Ideal/Ideal%20SHELF_10.glb?sp=r&st=2025-04-21T04:57:09Z&se=2026-04-21T12:57:09Z&spr=https&sv=2024-11-04&sr=b&sig=q9f9f1NQCnqDxsrFeKz1Z6DH47HAzPtgSXEioFWU%2Ft0%3D'
    ]
  },
  regular: {
    SHELF_1: [
      'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Shelves/SHELF_1.glb?sp=r&st=2025-04-14T06:37:26Z&se=2026-04-14T14:37:26Z&spr=https&sv=2024-11-04&sr=b&sig=NH7pxb44NTzuUy7xmCnXJGpQgVLxFERpd8TvaOCj4Tc%3D'
    ],
    SHELF_2: [
      'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Shelves/SHELF_2.glb?sp=r&st=2025-04-14T07:02:45Z&se=2026-04-14T15:02:45Z&spr=https&sv=2024-11-04&sr=b&sig=hebgNFgYN3fQOxj3R7NDGQSeCpRTvZxyxFcbJZFdPVw%3D'
    ],
    SHELF_3: [
      'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Shelves/SHELF_3.glb?sp=r&st=2025-04-14T07:03:33Z&se=2026-04-14T15:03:33Z&spr=https&sv=2024-11-04&sr=b&sig=gFnozzBHQvptvhkqrxvUDsd7r4SgheV0%2FcQYugrrfNM%3D'
    ],
    SHELF_4: [
      'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Shelves/SHELF_4.glb?sp=r&st=2025-04-14T07:04:18Z&se=2026-04-14T15:04:18Z&spr=https&sv=2024-11-04&sr=b&sig=VBPTxO%2FlwulBtMigHbPWSB4V%2FaTLl9Pr%2Fw795pLQfsI%3D'
    ],
    SHELF_5: [
      'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Shelves/SHELF_5.glb?sp=r&st=2025-04-14T07:04:40Z&se=2026-04-14T15:04:40Z&spr=https&sv=2024-11-04&sr=b&sig=swpuapR7zel8D%2FpVSBp54XbTX1mW%2FyUS0EgAs1FdkaI%3D'
    ],
    SHELF_6: [
      'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Shelves/SHELF_6.glb?sp=r&st=2025-04-14T07:04:56Z&se=2026-04-14T15:04:56Z&spr=https&sv=2024-11-04&sr=b&sig=CVMGZiOjCzjEHg3Ow5EZUSA5incOBEzAd4mj2WQtmHQ%3D'
    ],
    SHELF_7: [
      'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Shelves/SHELF_7.glb?sp=r&st=2025-04-14T07:05:11Z&se=2026-04-14T15:05:11Z&spr=https&sv=2024-11-04&sr=b&sig=TClMXdYnf29umy%2Bw6aZPdsjyZ5LHI7jy%2FvKFqB7SSJ8%3D'
    ],
    SHELF_8: [
      'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Shelves/SHELF_8.glb?sp=r&st=2025-04-14T07:05:27Z&se=2026-04-14T15:05:27Z&spr=https&sv=2024-11-04&sr=b&sig=gRwphw1rXJUem%2Fjt9VA33wmEos3OF5eDVwTCuKmv7sk%3D'
    ],
    SHELF_9: [
      'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Shelves/SHELF_9.glb?sp=r&st=2025-04-14T07:05:49Z&se=2026-04-14T15:05:49Z&spr=https&sv=2024-11-04&sr=b&sig=7YuWhpDTxSEpmk%2BmGJk9IPafe13TujLgAiaLMNdtQTE%3D'
    ],
    SHELF_10: [
      'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Shelves/SHELF_10.glb?sp=r&st=2025-04-14T07:06:04Z&se=2026-04-14T15:06:04Z&spr=https&sv=2024-11-04&sr=b&sig=7nu0qXmx7fqMbXCedYM3Y0bk2vRnqPJQl%2F6X%2FijDAJI%3D'
    ]
  }
};

// 2D Image URLs (to be filled later)
const imageURLs = {
  SHELF_1:
    'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Images/Images_SHELF_1.jpg?sp=r&st=2025-04-15T11:43:32Z&se=2026-04-15T19:43:32Z&spr=https&sv=2024-11-04&sr=b&sig=Li2hEwUd5%2F04qKWoclgIE4JtT7sBBg0TyTVhMaJDpUw%3D',
  SHELF_2:
    'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Images/Images_SHELF_2.jpg?sp=r&st=2025-04-15T11:44:19Z&se=2026-04-15T19:44:19Z&spr=https&sv=2024-11-04&sr=b&sig=%2F%2B%2BSiAivVICEBaxZWpatsi9PyXAXO4NjCOTB6Z%2FqAIg%3D',
  SHELF_3:
    'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Images/Images_SHELF_3.jpg?sp=r&st=2025-04-15T11:44:36Z&se=2026-04-15T19:44:36Z&spr=https&sv=2024-11-04&sr=b&sig=6H0Dn281RypDl%2FHHs7j4O2UUquvPa9M3lonc%2BeLM4uU%3D',
  SHELF_4:
    'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Images/Images_SHELF_4.jpg?sp=r&st=2025-04-15T11:44:59Z&se=2026-04-15T19:44:59Z&spr=https&sv=2024-11-04&sr=b&sig=4tbjPYFRtSlzOUk9fJEW%2BlWcUTcX2rA0JvCj5HquZo8%3D',
  SHELF_5:
    'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Images/Images_SHELF_5.jpg?sp=r&st=2025-04-15T11:45:17Z&se=2026-04-15T19:45:17Z&spr=https&sv=2024-11-04&sr=b&sig=vQyCA96GQ1sCAW0N2UbczNquQlwJ6zeZWhjBSUd9dBA%3D',
  SHELF_6:
    'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Images/Images_SHELF_6.jpg?sp=r&st=2025-04-15T11:45:32Z&se=2026-04-15T19:45:32Z&spr=https&sv=2024-11-04&sr=b&sig=qIaXFN4XGynMWJzbKuLwNEZrM4jzlCW69rLAuGzS%2Fj0%3D',
  SHELF_7:
    'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Images/Images_SHELF_7.jpg?sp=r&st=2025-04-15T11:45:47Z&se=2026-04-15T19:45:47Z&spr=https&sv=2024-11-04&sr=b&sig=IEVSTTc73ehJHH6w69lUE8ROaOV8b6noeAlMrVs2oGo%3D',
  SHELF_8:
    'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Images/Images_SHELF_8.jpg?sp=r&st=2025-04-15T11:46:05Z&se=2026-04-15T19:46:05Z&spr=https&sv=2024-11-04&sr=b&sig=osBbbkCPdCB9Z623i6dmWlWrbuFkVDYurkj%2BzHpxT0w%3D',
  SHELF_9:
    'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Images/Images_SHELF_9.jpg?sp=r&st=2025-04-15T11:46:18Z&se=2026-04-15T19:46:18Z&spr=https&sv=2024-11-04&sr=b&sig=nqESWkeVlgIL0iJJjgolHsVKvJ5kTx8J8oksR3t6O7c%3D',
  SHELF_10:
    'https://stdishadev2.blob.core.windows.net/tira-digital-twin/Images/Images_SHELF_10.jpg?sp=r&st=2025-04-15T11:43:58Z&se=2026-04-15T19:43:58Z&spr=https&sv=2024-11-04&sr=b&sig=F54t0Z4Yb%2BlW1pQa18KXgSbe%2FFsTTcHy1FCoD7qSPzU%3D'
};

const getShelfModelURL = (shelf, isIdeal) => {
  const key = isIdeal ? 'ideal' : 'regular';
  return modelURLs[key][shelf] || [];
};

// Only preload TIRA model initially
useGLTF.preload(TIRA_MODEL_URL);

// Preload models
//const shelfButtons = ['SHELF_1', 'SHELF_2', 'SHELF_3', 'SHELF_4', 'SHELF_5', 'SHELF_6', 'SHELF_7', 'SHELF_8', 'SHELF_9', 'SHELF_10'];
const SHELF_NAMES = ['SHELF_1', 'SHELF_2', 'SHELF_3', 'SHELF_4', 'SHELF_5', 'SHELF_6', 'SHELF_7', 'SHELF_8', 'SHELF_9', 'SHELF_10'];

function Loader() {
  const { active, progress, errors, item } = useProgress();
  const [showLoader, setShowLoader] = useState(false);

  useEffect(() => {
    // Only show loader if loading takes more than 100ms
    const timer = setTimeout(() => setShowLoader(true), 100);
    return () => clearTimeout(timer);
  }, []);

  if (!active || !showLoader) return null;

  return (
    <Html center>
      {/* <CircularProgress variant="determinate" value={progress} size={60} thickness={4} /> */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          bgcolor: 'rgba(255, 255, 255, 0.9)',
          p: 3,
          borderRadius: 2,
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          minWidth: '200px',
          marginBottom: '350px'
        }}
      >
        <ModelLoader />
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>
            Loading 3D Model
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {Math.round(progress)}% loaded
          </Typography>
          {item && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Loading: {item}
            </Typography>
          )}
          {errors.length > 0 && (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
              Error loading model. Please try refreshing.
            </Typography>
          )}
        </Box>
      </Box>
    </Html>
  );
}

export default function FullscreenView() {
  const navigate = useNavigate();
  const { store } = useParams();
  const location = useLocation();
  const [selectedShelf, setSelectedShelf] = useState(null);
  const [highlightedGroup, setHighlightedGroup] = useState(null);
  const [isIdealMode, setIsIdealMode] = useState(false);
  const [showTIRA, setShowTIRA] = useState(true);
  const [cameraPosition, setCameraPosition] = useState([0, 1, 4]);
  const [isDragMode, setIsDragMode] = useState(false);
  const controlsRef = useRef();
  const cameraRef = useRef();
  const [is3DMode, setIs3DMode] = useState(true);

  // Effect to handle URL params and set initial state
  useEffect(() => {
    console.log('Current URL:', location.search);
    const queryParams = new URLSearchParams(location.search);
    const shelf = queryParams.get('shelf');
    const ideal = queryParams.get('ideal') === 'true';
    const viewMode = queryParams.get('view') || '3D'; // Default to 3D

    console.log('URL Params - shelf:', shelf, 'ideal:', ideal, 'view:', viewMode);

    setIsIdealMode(ideal);
    setIs3DMode(viewMode === '3D'); // Ensure the toggle is correct

    if (shelf && SHELF_NAMES.includes(shelf)) {
      console.log('Setting selected shelf:', shelf);
      setSelectedShelf(shelf);
      setShowTIRA(false);
    } else {
      console.log('No valid shelf in URL, showing TIRA model');
      setSelectedShelf(null);
      setShowTIRA(true);
    }
  }, [location.search]);

  console.log('is3DMode:', is3DMode);
  console.log('selectedShelf:', selectedShelf);
  console.log('Image URL:', `https://storage.googleapis.com/3dmodelhost/Images/${selectedShelf}.jpg`);

  const handleStoreButton = () => {
    console.log('Store button clicked');
    setShowTIRA(true);
    setSelectedShelf(null);
    navigate(`/main/stores/storeinsight/fullscreen/${store}`);
    window.location.reload();
  };

  const handleShelfClick = async (shelf) => {
    console.log('Shelf clicked:', shelf);
    try {
      setShowTIRA(false);
      setSelectedShelf(shelf);
      const newUrl = `/main/stores/storeinsight/fullscreen/${store}?shelf=${shelf}&ideal=${isIdealMode}&view=${is3DMode ? '3D' : '2D'}`;
      console.log('Navigating to:', newUrl);
      navigate(newUrl);
      window.location.reload();
    } catch (error) {
      console.error('Error handling shelf click:', error);
    }
  };
  const handleModeToggle = () => {
    const newIdealMode = !isIdealMode;
    setIsIdealMode(newIdealMode);
    if (selectedShelf) {
      const newUrl = `/main/stores/storeinsight/fullscreen/${store}?shelf=${selectedShelf}&ideal=${newIdealMode}&view=${
        is3DMode ? '3D' : '2D'
      }`;
      navigate(newUrl);
      // window.location.reload();
    }
  };
  const handleViewModeToggle = () => {
    const new3DMode = !is3DMode;
    setIs3DMode(new3DMode);

    if (selectedShelf) {
      const newUrl = `/main/stores/storeinsight/fullscreen/${store}?shelf=${selectedShelf}&ideal=${isIdealMode}&view=${
        new3DMode ? '3D' : '2D'
      }`;

      navigate(newUrl);
      // window.location.reload();
    }
  };

  // Camera controls
  const handleZoomIn = () => {
    if (controlsRef.current) {
      controlsRef.current.dollyOut(1.3);
    }
  };

  const handleZoomOut = () => {
    if (controlsRef.current) {
      controlsRef.current.dollyIn(1.3);
    }
  };

  const resetCamera = () => {
    if (controlsRef.current) {
      controlsRef.current.reset();
      setCameraPosition([0, 1, 4]);
    }
  };

  const toggleDragMode = () => {
    setIsDragMode(!isDragMode);
    if (controlsRef.current) {
      // When isDragMode is true, we disable rotation and enable panning
      controlsRef.current.enableRotate = !isDragMode;
      controlsRef.current.enablePan = isDragMode;
    }
  };

  // Clean up memory when component unmounts
  useEffect(() => {
    return () => {
      // Dispose of loaded models when component unmounts
      useGLTF.disposeAll();
    };
  }, []);

  return (
    <div className="fixed top-[63px] left-0 w-full h-[calc(100vh-63px)] bg-white z-10 flex flex-col">
      {/* Header with navigation buttons */}
      <div className="flex justify-between items-center p-4">
        <button
          onClick={() => {
            console.log('Navigating to Overview page');
            navigate(`/main/stores/storeinsight/overview/${store}`);
            setTimeout(() => {
              window.location.reload();
            }, 100);
          }}
          className="px-4 py-2 bg-gray-200 text-black rounded-lg hover:bg-gray-300 transition flex items-center gap-2"
        >
          <ArrowBack />
          Overview
        </button>
      </div>

      {/* Shelf selection buttons */}
      <div className="flex flex-wrap gap-2 p-4">
        <button
          onClick={handleStoreButton}
          className={`px-4 py-2 rounded-lg transition ${showTIRA ? 'bg-black text-white' : 'bg-gray-200 text-black hover:bg-gray-300'}`}
        >
          STORE
        </button>
        {SHELF_NAMES.map((shelf) => (
          <button
            key={shelf}
            onClick={() => handleShelfClick(shelf)}
            onMouseEnter={() => setHighlightedGroup(shelf)}
            onMouseLeave={() => setHighlightedGroup(null)}
            className={`px-4 py-2 rounded-lg transition ${
              selectedShelf === shelf ? 'bg-black text-white' : 'bg-gray-200 text-black hover:bg-gray-300'
            }`}
          >
            {shelf}
          </button>
        ))}
        {selectedShelf && (
          <div className="flex items-center gap-4 ml-4">
            {/* Current/Ideal Mode Toggle */}
            <div className="flex items-center gap-2">
              <span className={`text-sm ${!isIdealMode ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>Current</span>
              <ToggleSwitch checked={isIdealMode} onCheckedChange={handleModeToggle} size="md" />
              <span className={`text-sm ${isIdealMode ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>Ideal</span>
            </div>

            {/* 2D/3D Mode Toggle */}
            <div className="flex items-center gap-2">
              <span className={`text-sm ${!is3DMode ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>2D</span>
              <ToggleSwitch checked={is3DMode} onCheckedChange={handleViewModeToggle} size="md" />
              <span className={`text-sm ${is3DMode ? 'text-blue-600 font-medium' : 'text-gray-500'}`}>3D</span>
            </div>
          </div>
        )}
      </div>

      {/* 3D Model Canvas */}
      <div className="relative flex-grow">
        {is3DMode ? (
          <Canvas shadows camera={{ position: [0, 1, 4], fov: 45 }}>
            <PerspectiveCamera makeDefault position={cameraPosition} fov={45} ref={cameraRef} near={0.1} far={1000} />
            <ambientLight intensity={0.5} />
            <hemisphereLight intensity={0.6} />
            <directionalLight position={[5, 5, 5]} intensity={0.5} />
            <Suspense fallback={<Loader />}>
              {selectedShelf ? (
                <ErrorBoundary fallback={<Html center>Error loading shelf model</Html>}>
                  <Shelf selectedShelf={selectedShelf} isIdealMode={isIdealMode} />
                </ErrorBoundary>
              ) : (
                <ErrorBoundary fallback={<Html center>Error loading TIRA model</Html>}>
                  <TIRAFullModel highlightedGroup={highlightedGroup} />
                </ErrorBoundary>
              )}
            </Suspense>
            <OrbitControls
              ref={controlsRef}
              enablePan={isDragMode}
              enableZoom={true}
              enableRotate={!isDragMode}
              minDistance={2}
              maxDistance={20}
              minPolarAngle={0}
              maxPolarAngle={Math.PI}
              dampingFactor={0.1}
              rotateSpeed={0.7}
              zoomSpeed={0.8}
              panSpeed={1.2}
              enableDamping={true}
              target={[0, 1, 0]}
              mouseButtons={{
                LEFT: isDragMode ? 2 : 0,
                MIDDLE: 1,
                RIGHT: 0
              }}
            />
          </Canvas>
        ) : (
          <div className="w-full h-full flex justify-center items-center bg-gray-100">
            <div className="w-3/4 md:w-2/3 lg:w-1/2 h-auto">
              <img
                src={imageURLs[selectedShelf] || ''}
                alt={`2D ${selectedShelf}`}
                className="w-full h-auto object-contain shadow-lg rounded-lg"
              />
            </div>
          </div>
        )}

        {/* Controls UI (Visible only in 3D Mode) */}
        {is3DMode && (
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex items-center gap-3 bg-white/95 px-4 py-2 rounded-full shadow-lg">
            <Tooltip title="Zoom In">
              <IconButton onClick={handleZoomIn} size="small" className="hover:bg-gray-100">
                <ZoomIn />
              </IconButton>
            </Tooltip>
            <div className="h-4 w-px bg-gray-300" />
            <Tooltip title="Zoom Out">
              <IconButton onClick={handleZoomOut} size="small" className="hover:bg-gray-100">
                <ZoomOut />
              </IconButton>
            </Tooltip>
            <div className="h-4 w-px bg-gray-300" />
            <Tooltip title="Reset View">
              <IconButton onClick={resetCamera} size="small" className="hover:bg-gray-100">
                <CenterFocusStrong />
              </IconButton>
            </Tooltip>
            <div className="h-4 w-px bg-gray-300" />
            <Tooltip title={isDragMode ? 'Switch to Rotation Mode' : 'Switch to Pan Mode'}>
              <IconButton onClick={toggleDragMode} size="small" className={`hover:bg-gray-100 ${isDragMode ? 'bg-blue-50' : ''}`}>
                {isDragMode ? <ThreeSixty /> : <PanTool />}
              </IconButton>
            </Tooltip>
            <div className="h-4 w-px bg-gray-300" />
            <Tooltip title="Toggle Auto-Rotation">
              <IconButton
                onClick={() => {
                  if (controlsRef.current) {
                    controlsRef.current.autoRotate = !controlsRef.current.autoRotate;
                    controlsRef.current.autoRotateSpeed = 2.0;
                  }
                }}
                size="small"
                className="hover:bg-gray-100"
              >
                <ThreeDRotation />
              </IconButton>
            </Tooltip>
          </div>
        )}
      </div>
    </div>
  );
}

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback;
    }
    return this.props.children;
  }
}

const TIRAFullModel = ({ highlightedGroup }) => {
  const groupRef = useRef(null);

  const { scene } = useGLTF(TIRA_MODEL_URL, {
    draco: true,
    meshoptSimplifier: true,
    onProgress: (progress) => {
      console.log('TIRA Model loading progress:', progress);
    }
  });

  useEffect(() => {
    if (groupRef.current && highlightedGroup) {
      const group = groupRef.current.getObjectByName(highlightedGroup);
      if (group) {
        group.traverse((child) => {
          if (child.isMesh) {
            child.material.emissive = { r: 1, g: 0, b: 0 };
            console.log(`Highlighting group: ${highlightedGroup}`);
          }
        });
      }
    } else if (groupRef.current) {
      groupRef.current.traverse((child) => {
        if (child.isMesh) {
          child.material.emissive = { r: 0, g: 0, b: 0 };
        }
      });
    }
  }, [highlightedGroup]);

  return <primitive object={scene} ref={groupRef} scale={[2, 2, 2]} />;
};

const Shelf = ({ selectedShelf, isIdealMode }) => {
  const modelUrls = getShelfModelURL(selectedShelf, isIdealMode);
  const modelUrl = modelUrls[0];
  const { scene } = useGLTF(modelUrl, {
    draco: true,
    meshopt: true
  });

  if (!modelUrl) {
    return null;
  }

  return <primitive object={scene} scale={[5, 5, 5]} />;
};
